import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useLocationPathname, useLocationState, useNavigate, useParams } from '@rocketlane/rocket-router';
import { PageLoader } from '@rocketlane/ui';
import React, { Suspense, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';
import { useWithAccess } from 'hooks/rbac/useWithAccess';
import { useAgentBuilderHostAPI } from 'hooks/use-agent-builder-host-api';
import { useTranslation } from 'hooks/use-translation';
import { useAccountDataQuery } from 'queries/account';
import { NotFound } from 'routes/404';
// Lazy load the federated component from signals remote
const DocumentAgentPreview = /*#__PURE__*/ React.lazy(()=>import('signals/DocumentAgentPreview'));
const StyledDocumentAgentPreviewPage = styled.div`
  background-color: var(--scarlet-gray-80);
`;
/**
 * Federated DocumentAgentPreviewPage component that can be consumed by the parent application
 * This component loads DocumentAgentPreview from the signals remote (rocket-ai-frontend)
 * It extracts documentationAgentId from URL params and passes it via location state
 */ export const DocumentAgentPreviewPage = ()=>{
    const { documentationAgentId } = useParams();
    const pathname = useLocationPathname();
    const locState = useLocationState();
    const navigate = useNavigate();
    const tabTitle = useTranslation('document_agent.preview.tab_title');
    const { hasAccessToResource } = useWithAccess();
    const documentAgentPermissions = useMemo(()=>({
            read: hasAccessToResource({
                rule: 'DOCUMENT_AGENT:READ'
            }),
            write: hasAccessToResource({
                rule: 'DOCUMENT_AGENT:WRITE'
            }),
            delete: hasAccessToResource({
                rule: 'DOCUMENT_AGENT:DELETE'
            }),
            create: hasAccessToResource({
                rule: 'DOCUMENT_AGENT:CREATE'
            }),
            invoke: hasAccessToResource({
                rule: 'DOCUMENT_AGENT:CREATE'
            })
        }), [
        hasAccessToResource
    ]);
    // Ensure documentationAgentId is in location state for the remote component
    // Always sync URL param to state, ensuring URL param takes precedence over stale state
    useEffect(()=>{
        if (documentationAgentId) {
            const urlParamId = parseInt(documentationAgentId, 10);
            // Validate that the parsed ID is a valid number
            if (!Number.isNaN(urlParamId) && Number.isFinite(urlParamId)) {
                const stateId = locState === null || locState === void 0 ? void 0 : locState.documentationAgentId;
                // Update state if: no state ID exists, state ID doesn't match URL param, or state hasn't been set
                if (!stateId || stateId !== urlParamId || !(locState === null || locState === void 0 ? void 0 : locState._hasSetState)) {
                    navigate(pathname, {
                        state: {
                            ...locState,
                            documentationAgentId: urlParamId,
                            _hasSetState: true
                        },
                        replace: true
                    });
                }
            }
        // If invalid ID, don't update state - let the remote component handle the error
        }
    }, [
        documentationAgentId,
        pathname,
        locState,
        navigate
    ]);
    const hostAPI = useAgentBuilderHostAPI();
    return /*#__PURE__*/ _jsxs(StyledDocumentAgentPreviewPage, {
        className: "document-agent-preview-page w-full h-full",
        children: [
            /*#__PURE__*/ _jsx(Helmet, {
                children: /*#__PURE__*/ _jsx("title", {
                    children: tabTitle
                })
            }),
            /*#__PURE__*/ _jsx(Suspense, {
                fallback: /*#__PURE__*/ _jsx(PageLoader, {}),
                children: /*#__PURE__*/ _jsx(DocumentAgentPreview, {
                    hostAPI: hostAPI,
                    documentAgentPermissions: documentAgentPermissions,
                    hasAccessToResource: hasAccessToResource
                })
            })
        ]
    });
};
/**
 * Renders DocumentAgentPreviewPage only when documentationAgent feature is enabled; otherwise NotFound.
 */ export const DocumentAgentPreviewRouteGuard = ()=>{
    var _ref;
    var _accountData_accountMeta_features, _accountData_accountMeta;
    const { data: accountData, isLoading } = useAccountDataQuery();
    const isDocumentationAgentEnabled = (_ref = accountData === null || accountData === void 0 ? void 0 : (_accountData_accountMeta = accountData.accountMeta) === null || _accountData_accountMeta === void 0 ? void 0 : (_accountData_accountMeta_features = _accountData_accountMeta.features) === null || _accountData_accountMeta_features === void 0 ? void 0 : _accountData_accountMeta_features.documentationAgent) !== null && _ref !== void 0 ? _ref : false;
    if (isLoading) {
        return /*#__PURE__*/ _jsx(PageLoader, {});
    }
    if (!isDocumentationAgentEnabled) {
        return /*#__PURE__*/ _jsx(NotFound, {});
    }
    return /*#__PURE__*/ _jsx(DocumentAgentPreviewPage, {});
};
